import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clientsService } from '../../services/clientsService';
import { ClientResponse, ClientStatus, ClientPlatform } from '../../types/clients';
import { OrderStatus } from '../../types/orders';
import Modal from '../modals/Modal';
import './Page.css';

type BooleanFilter = '' | 'true' | 'false';

type ClientFiltersDraft = {
  hasOrders: BooleanFilter;
  onlyCancelledOrders: BooleanFilter;
  lastCompletedOrderBefore: string;
  lastCompletedOrderAfter: string;
  lastCompletedOrderBetweenStart: string;
  lastCompletedOrderBetweenEnd: string;
  lastCompletedOrderWithinDays: string;
  minCompletedOrders: string;
  maxCompletedOrders: string;
  orderStatusIn: OrderStatus[];
  createdAtFrom: string;
  createdAtTo: string;
  platform: ClientPlatform | '';
};

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [clients, setClients] = useState<ClientResponse[]>([]);
  const [clientsTotal, setClientsTotal] = useState<number | null>(null);
  const [clientsTotalPages, setClientsTotalPages] = useState<number | null>(null);
  const [clientsCurrentPage, setClientsCurrentPage] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtersError, setFiltersError] = useState('');
  const [showInfoExamples, setShowInfoExamples] = useState(false);
  const [isPushModalOpen, setIsPushModalOpen] = useState(false);
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [pushImageUrl, setPushImageUrl] = useState('');
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState('');
  const [pushSuccessId, setPushSuccessId] = useState<number | null>(null);
  const [infoDaysOrdered, setInfoDaysOrdered] = useState('10');
  const [infoDaysNoOrder, setInfoDaysNoOrder] = useState('10');
  const [infoDaysNewClients, setInfoDaysNewClients] = useState('7');
  const [infoAfterDate, setInfoAfterDate] = useState('2025-12-10T00:00');
  const [infoVeryActiveMin, setInfoVeryActiveMin] = useState('10');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const pageSize = 20;

  // Read from URL params or use defaults
  const searchQuery = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const statusFilter = searchParams.get('status') as ClientStatus | null;

  const isValidOrderStatus = (value: string | null): value is OrderStatus => {
    return value !== null && Object.values(OrderStatus).includes(value as OrderStatus);
  };

  const isValidPlatform = (value: string | null): value is ClientPlatform => {
    return value !== null && Object.values(ClientPlatform).includes(value as ClientPlatform);
  };

  const UZT_OFFSET_MINUTES = 300;
  const UZT_OFFSET_MS = UZT_OFFSET_MINUTES * 60 * 1000;

  const formatDateTimeLocal = (date: Date): string => {
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}`;
  };

  const formatDateTimeLocalUzt = (epochMillis: number): string => {
    const date = new Date(epochMillis + UZT_OFFSET_MS);
    const pad = (value: number) => value.toString().padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
      date.getUTCDate()
    )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
  };

  const parseDateTimeLocalAsUzt = (value: string): number | undefined => {
    if (!value) return undefined;
    const [datePart, timePart] = value.split('T');
    if (!datePart || !timePart) return undefined;
    const [yearStr, monthStr, dayStr] = datePart.split('-');
    const [hourStr, minuteStr] = timePart.split(':');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    const hour = Number(hourStr);
    const minute = Number(minuteStr);
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day) ||
      !Number.isFinite(hour) ||
      !Number.isFinite(minute)
    ) {
      return undefined;
    }
    const utcMillis = Date.UTC(year, month - 1, day, hour, minute) - UZT_OFFSET_MS;
    return Number.isFinite(utcMillis) ? utcMillis : undefined;
  };

  const getUztStartOfDayEpoch = (epochMillis: number): number => {
    const uztDate = new Date(epochMillis + UZT_OFFSET_MS);
    const startUtcMillis = Date.UTC(
      uztDate.getUTCFullYear(),
      uztDate.getUTCMonth(),
      uztDate.getUTCDate(),
      0,
      0
    );
    return startUtcMillis - UZT_OFFSET_MS;
  };

  const toDateTimeLocal = (epochMillis: string | null): string => {
    if (!epochMillis) return '';
    const ms = Number(epochMillis);
    if (!Number.isFinite(ms)) return '';
    return formatDateTimeLocalUzt(ms);
  };

  const fromDateTimeLocal = (value: string): number | undefined => {
    return parseDateTimeLocalAsUzt(value);
  };

  const parseBooleanParam = (value: string | null): boolean | undefined => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  };

  const parseNumberParam = (value: string | null): number | undefined => {
    if (!value) return undefined;
    const num = Number(value);
    if (!Number.isFinite(num)) return undefined;
    return num;
  };

  const parseOrderStatusList = (value: string | null): OrderStatus[] => {
    if (!value) return [];
    return value
      .split(',')
      .map((status) => status.trim())
      .filter((status): status is OrderStatus => isValidOrderStatus(status));
  };

  const parseDraftFilters = (params: URLSearchParams): ClientFiltersDraft => {
    const orderStatuses = parseOrderStatusList(params.get('orderStatusIn'));

    const hasOrders = params.get('hasOrders');
    const onlyCancelledOrders = params.get('onlyCancelledOrders');

    return {
      hasOrders: hasOrders === 'true' || hasOrders === 'false' ? hasOrders : '',
      onlyCancelledOrders:
        onlyCancelledOrders === 'true' || onlyCancelledOrders === 'false' ? onlyCancelledOrders : '',
      lastCompletedOrderBefore: toDateTimeLocal(params.get('lastCompletedOrderBefore')),
      lastCompletedOrderAfter: toDateTimeLocal(params.get('lastCompletedOrderAfter')),
      lastCompletedOrderBetweenStart: toDateTimeLocal(params.get('lastCompletedOrderBetweenStart')),
      lastCompletedOrderBetweenEnd: toDateTimeLocal(params.get('lastCompletedOrderBetweenEnd')),
      lastCompletedOrderWithinDays: params.get('lastCompletedOrderWithinDays') || '',
      minCompletedOrders: params.get('minCompletedOrders') || '',
      maxCompletedOrders: params.get('maxCompletedOrders') || '',
      orderStatusIn: orderStatuses,
      createdAtFrom: toDateTimeLocal(params.get('createdAtFrom')),
      createdAtTo: toDateTimeLocal(params.get('createdAtTo')),
      platform: isValidPlatform(params.get('platform')) ? (params.get('platform') as ClientPlatform) : ''
    };
  };

  const [filters, setFilters] = useState<ClientFiltersDraft>(() => parseDraftFilters(searchParams));

  useEffect(() => {
    setFilters(parseDraftFilters(searchParams));
  }, [searchParams]);

  const appliedFilters = useMemo(() => {
    const orderStatusIn = parseOrderStatusList(searchParams.get('orderStatusIn'));

    const result = {
      hasOrders: parseBooleanParam(searchParams.get('hasOrders')),
      onlyCancelledOrders: parseBooleanParam(searchParams.get('onlyCancelledOrders')),
      lastCompletedOrderBefore: parseNumberParam(searchParams.get('lastCompletedOrderBefore')),
      lastCompletedOrderAfter: parseNumberParam(searchParams.get('lastCompletedOrderAfter')),
      lastCompletedOrderBetweenStart: parseNumberParam(searchParams.get('lastCompletedOrderBetweenStart')),
      lastCompletedOrderBetweenEnd: parseNumberParam(searchParams.get('lastCompletedOrderBetweenEnd')),
      lastCompletedOrderWithinDays: parseNumberParam(searchParams.get('lastCompletedOrderWithinDays')),
      minCompletedOrders: parseNumberParam(searchParams.get('minCompletedOrders')),
      maxCompletedOrders: parseNumberParam(searchParams.get('maxCompletedOrders')),
      orderStatusIn,
      createdAtFrom: parseNumberParam(searchParams.get('createdAtFrom')),
      createdAtTo: parseNumberParam(searchParams.get('createdAtTo')),
      platform: isValidPlatform(searchParams.get('platform')) ? (searchParams.get('platform') as ClientPlatform) : undefined
    };

    const clearCompleted = () => {
      result.minCompletedOrders = undefined;
      result.maxCompletedOrders = undefined;
      result.lastCompletedOrderBefore = undefined;
      result.lastCompletedOrderAfter = undefined;
      result.lastCompletedOrderBetweenStart = undefined;
      result.lastCompletedOrderBetweenEnd = undefined;
      result.lastCompletedOrderWithinDays = undefined;
    };

    if (result.hasOrders === false) {
      result.onlyCancelledOrders = undefined;
      result.orderStatusIn = [];
      clearCompleted();
    }

    if (result.onlyCancelledOrders === true) {
      clearCompleted();
    }

    if (result.lastCompletedOrderWithinDays !== undefined) {
      result.lastCompletedOrderBefore = undefined;
      result.lastCompletedOrderAfter = undefined;
      result.lastCompletedOrderBetweenStart = undefined;
      result.lastCompletedOrderBetweenEnd = undefined;
    }

    return result;
  }, [searchParams]);

  const clearCompletedOrderFilters = (draft: ClientFiltersDraft) => {
    draft.minCompletedOrders = '';
    draft.maxCompletedOrders = '';
    draft.lastCompletedOrderBefore = '';
    draft.lastCompletedOrderAfter = '';
    draft.lastCompletedOrderBetweenStart = '';
    draft.lastCompletedOrderBetweenEnd = '';
    draft.lastCompletedOrderWithinDays = '';
  };

  const updateFilters = (patch: Partial<ClientFiltersDraft>) => {
    setFilters((prev) => {
      const next: ClientFiltersDraft = { ...prev, ...patch };

      if (next.onlyCancelledOrders === 'true' && next.hasOrders !== 'true') {
        next.hasOrders = 'true';
      }

      const hasOrdersFalse = next.hasOrders === 'false';
      if (hasOrdersFalse) {
        next.onlyCancelledOrders = '';
        next.orderStatusIn = [];
        clearCompletedOrderFilters(next);
      }

      if (next.onlyCancelledOrders === 'true') {
        clearCompletedOrderFilters(next);
      }

      const hasWithinDays = next.lastCompletedOrderWithinDays !== '';
      if (hasWithinDays) {
        next.lastCompletedOrderBefore = '';
        next.lastCompletedOrderAfter = '';
        next.lastCompletedOrderBetweenStart = '';
        next.lastCompletedOrderBetweenEnd = '';
      }

      const hasOtherLastCompletedFilters =
        next.lastCompletedOrderBefore ||
        next.lastCompletedOrderAfter ||
        next.lastCompletedOrderBetweenStart ||
        next.lastCompletedOrderBetweenEnd;
      if (hasOtherLastCompletedFilters && next.lastCompletedOrderWithinDays) {
        next.lastCompletedOrderWithinDays = '';
      }

      return next;
    });
    setFiltersError('');
  };

  const validateFilters = (draft: ClientFiltersDraft): string | null => {
    const errors: string[] = [];
    const parseIntSafe = (value: string) => {
      if (value === '') return undefined;
      const num = Number(value);
      if (!Number.isFinite(num) || !Number.isInteger(num)) return NaN;
      return num;
    };

    const minCompletedOrders = parseIntSafe(draft.minCompletedOrders);
    const maxCompletedOrders = parseIntSafe(draft.maxCompletedOrders);
    const withinDays = parseIntSafe(draft.lastCompletedOrderWithinDays);

    if (minCompletedOrders !== undefined && Number.isNaN(minCompletedOrders)) {
      errors.push(t('clients.filterErrors.nonNegative'));
    } else if (minCompletedOrders !== undefined && minCompletedOrders < 0) {
      errors.push(t('clients.filterErrors.nonNegative'));
    }

    if (maxCompletedOrders !== undefined && Number.isNaN(maxCompletedOrders)) {
      errors.push(t('clients.filterErrors.nonNegative'));
    } else if (maxCompletedOrders !== undefined && maxCompletedOrders < 0) {
      errors.push(t('clients.filterErrors.nonNegative'));
    }

    if (
      minCompletedOrders !== undefined &&
      maxCompletedOrders !== undefined &&
      minCompletedOrders > maxCompletedOrders
    ) {
      errors.push(t('clients.filterErrors.minMaxOrder'));
    }

    if (withinDays !== undefined && (Number.isNaN(withinDays) || withinDays <= 0)) {
      errors.push(t('clients.filterErrors.withinDaysPositive'));
    }

    const betweenStart = fromDateTimeLocal(draft.lastCompletedOrderBetweenStart);
    const betweenEnd = fromDateTimeLocal(draft.lastCompletedOrderBetweenEnd);
    const after = fromDateTimeLocal(draft.lastCompletedOrderAfter);
    const before = fromDateTimeLocal(draft.lastCompletedOrderBefore);
    const createdAtFrom = fromDateTimeLocal(draft.createdAtFrom);
    const createdAtTo = fromDateTimeLocal(draft.createdAtTo);

    const betweenStartSet = draft.lastCompletedOrderBetweenStart !== '';
    const betweenEndSet = draft.lastCompletedOrderBetweenEnd !== '';
    if (betweenStartSet !== betweenEndSet) {
      errors.push(t('clients.filterErrors.betweenRequired'));
    } else if (betweenStart !== undefined && betweenEnd !== undefined && betweenStart >= betweenEnd) {
      errors.push(t('clients.filterErrors.betweenOrder'));
    }

    if (after !== undefined && before !== undefined && after >= before) {
      errors.push(t('clients.filterErrors.afterBeforeOrder'));
    }

    if (createdAtFrom !== undefined && createdAtTo !== undefined && createdAtFrom > createdAtTo) {
      errors.push(t('clients.filterErrors.createdAtOrder'));
    }

    const hasOrdersFalse = draft.hasOrders === 'false';
    const onlyCancelledTrue = draft.onlyCancelledOrders === 'true';
    const hasCompletedFilters =
      draft.minCompletedOrders !== '' ||
      draft.maxCompletedOrders !== '' ||
      draft.lastCompletedOrderBefore !== '' ||
      draft.lastCompletedOrderAfter !== '' ||
      draft.lastCompletedOrderBetweenStart !== '' ||
      draft.lastCompletedOrderBetweenEnd !== '' ||
      draft.lastCompletedOrderWithinDays !== '';

    if (hasOrdersFalse && (draft.orderStatusIn.length > 0 || hasCompletedFilters)) {
      errors.push(t('clients.filterErrors.invalidCombination'));
    }

    if (onlyCancelledTrue && hasCompletedFilters) {
      errors.push(t('clients.filterErrors.onlyCancelledNoCompleted'));
    }

    const hasWithinDays =
      draft.lastCompletedOrderWithinDays !== '' &&
      (draft.lastCompletedOrderBefore !== '' ||
        draft.lastCompletedOrderAfter !== '' ||
        draft.lastCompletedOrderBetweenStart !== '' ||
        draft.lastCompletedOrderBetweenEnd !== '');
    if (hasWithinDays) {
      errors.push(t('clients.filterErrors.withinDaysExclusive'));
    }

    return errors.length > 0 ? errors[0] : null;
  };

  // Helper functions to update URL params
  const updateSearchQuery = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updateStatusFilter = (status: ClientStatus | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (status) {
      newParams.set('status', status);
    } else {
      newParams.delete('status');
    }
    newParams.set('page', '0');
    setSearchParams(newParams);
  };

  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const applyFilters = (draftOverride?: ClientFiltersDraft) => {
    const draft = draftOverride || filters;
    const validationError = validateFilters(draft);
    setFiltersError(validationError || '');
    if (validationError) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', '0');

    const setOrDelete = (key: string, value?: string | number | null) => {
      if (value === undefined || value === null || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    };

    setOrDelete('hasOrders', draft.hasOrders || '');
    setOrDelete('onlyCancelledOrders', draft.onlyCancelledOrders || '');
    setOrDelete('minCompletedOrders', draft.minCompletedOrders);
    setOrDelete('maxCompletedOrders', draft.maxCompletedOrders);
    setOrDelete('lastCompletedOrderWithinDays', draft.lastCompletedOrderWithinDays);

    setOrDelete('lastCompletedOrderBefore', fromDateTimeLocal(draft.lastCompletedOrderBefore));
    setOrDelete('lastCompletedOrderAfter', fromDateTimeLocal(draft.lastCompletedOrderAfter));
    setOrDelete(
      'lastCompletedOrderBetweenStart',
      fromDateTimeLocal(draft.lastCompletedOrderBetweenStart)
    );
    setOrDelete(
      'lastCompletedOrderBetweenEnd',
      fromDateTimeLocal(draft.lastCompletedOrderBetweenEnd)
    );
    setOrDelete('createdAtFrom', fromDateTimeLocal(draft.createdAtFrom));
    setOrDelete('createdAtTo', fromDateTimeLocal(draft.createdAtTo));

    if (draft.orderStatusIn.length > 0) {
      newParams.set('orderStatusIn', draft.orderStatusIn.join(','));
    } else {
      newParams.delete('orderStatusIn');
    }

    if (draft.platform) {
      newParams.set('platform', draft.platform);
    } else {
      newParams.delete('platform');
    }

    setSearchParams(newParams);
  };

  const clearFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    [
      'hasOrders',
      'onlyCancelledOrders',
      'lastCompletedOrderBefore',
      'lastCompletedOrderAfter',
      'lastCompletedOrderBetweenStart',
      'lastCompletedOrderBetweenEnd',
      'lastCompletedOrderWithinDays',
      'minCompletedOrders',
      'maxCompletedOrders',
      'orderStatusIn',
      'createdAtFrom',
      'createdAtTo',
      'platform'
    ].forEach((key) => newParams.delete(key));
    newParams.set('page', '0');
    setSearchParams(newParams);
    setFilters(parseDraftFilters(newParams));
    setFiltersError('');
  };

  useEffect(() => {
    fetchClients();
  }, [searchQuery, currentPage, statusFilter, appliedFilters]);

  const fetchClients = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await clientsService.getClients({
        searchQuery,
        page: currentPage,
        pageSize,
        status: statusFilter,
        hasOrders: appliedFilters.hasOrders,
        onlyCancelledOrders: appliedFilters.onlyCancelledOrders,
        lastCompletedOrderBefore: appliedFilters.lastCompletedOrderBefore,
        lastCompletedOrderAfter: appliedFilters.lastCompletedOrderAfter,
        lastCompletedOrderBetweenStart: appliedFilters.lastCompletedOrderBetweenStart,
        lastCompletedOrderBetweenEnd: appliedFilters.lastCompletedOrderBetweenEnd,
        lastCompletedOrderWithinDays: appliedFilters.lastCompletedOrderWithinDays,
        minCompletedOrders: appliedFilters.minCompletedOrders,
        maxCompletedOrders: appliedFilters.maxCompletedOrders,
        orderStatusIn: appliedFilters.orderStatusIn.length > 0 ? appliedFilters.orderStatusIn : undefined,
        createdAtFrom: appliedFilters.createdAtFrom,
        createdAtTo: appliedFilters.createdAtTo,
        platform: appliedFilters.platform
      });

      if (response.success && response.data) {
        setClients(response.data.clients || []);
        setClientsTotal(response.data.totalClients);
        setClientsTotalPages(response.data.totalPages);
        setClientsCurrentPage(response.data.currentPage);
      } else {
        setError(response.error || 'Failed to fetch clients');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const search = formData.get('search') as string;
    updateSearchQuery(search);
  };

  const handleRowClick = (clientId: number) => {
    navigate(`/clients/${clientId}`);
  };

  const getLocalizedStatus = (status: ClientStatus): string => {
    switch (status) {
      case ClientStatus.ACTIVE:
        return t('clients.statusActive');
      case ClientStatus.BLOCKED:
        return t('clients.statusBlocked');
      default:
        return status;
    }
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasOrdersFalse = filters.hasOrders === 'false';
  const onlyCancelledTrue = filters.onlyCancelledOrders === 'true';
  const completedFiltersDisabled = hasOrdersFalse || onlyCancelledTrue;
  const lastCompletedFiltersDisabled = completedFiltersDisabled;
  const otherLastCompletedFiltersSet =
    filters.lastCompletedOrderBefore !== '' ||
    filters.lastCompletedOrderAfter !== '' ||
    filters.lastCompletedOrderBetweenStart !== '' ||
    filters.lastCompletedOrderBetweenEnd !== '';
  const withinDaysDisabled = completedFiltersDisabled || otherLastCompletedFiltersSet;
  const orderStatusDisabled = hasOrdersFalse;
  const validationError = validateFilters(filters);
  const infoDaysOrderedValue = Math.max(1, Number(infoDaysOrdered || 10));
  const infoDaysNoOrderValue = Math.max(1, Number(infoDaysNoOrder || 10));
  const infoDaysNewValue = Math.max(1, Number(infoDaysNewClients || 7));
  const infoVeryActiveValue = Math.max(1, Number(infoVeryActiveMin || 10));
  const nowEpoch = Date.now();
  const nowUztStart = getUztStartOfDayEpoch(nowEpoch);
  const orderedDaysStart = nowUztStart - infoDaysOrderedValue * 24 * 60 * 60 * 1000;
  const noOrderDaysStart = nowUztStart - infoDaysNoOrderValue * 24 * 60 * 60 * 1000;
  const newClientsStart = nowUztStart - infoDaysNewValue * 24 * 60 * 60 * 1000;
  const infoAfterEpoch = parseDateTimeLocalAsUzt(infoAfterDate) ?? Date.UTC(2025, 11, 10, 0, 0, 0);
  const formattedAfterDate = formatDateTimeLocalUzt(infoAfterEpoch).split('T')[0].split('-').reverse().join('/');

  const advancedParamKeys = [
    'hasOrders',
    'onlyCancelledOrders',
    'lastCompletedOrderBefore',
    'lastCompletedOrderAfter',
    'lastCompletedOrderBetweenStart',
    'lastCompletedOrderBetweenEnd',
    'lastCompletedOrderWithinDays',
    'minCompletedOrders',
    'maxCompletedOrders',
    'orderStatusIn',
    'createdAtFrom',
    'createdAtTo',
    'platform'
  ];
  const hasAdvancedParams = advancedParamKeys.some((key) => searchParams.get(key));

  const applyExample = (overrides: Partial<ClientFiltersDraft>) => {
    const baseParams = new URLSearchParams(searchParams);
    advancedParamKeys.forEach((key) => baseParams.delete(key));
    const baseFilters = parseDraftFilters(baseParams);
    const merged: ClientFiltersDraft = { ...baseFilters, ...overrides };
    setFilters(merged);
    setFiltersError('');
    applyFilters(merged);
  };

  const openPushModal = () => {
    setPushError('');
    setPushSuccessId(null);
    setIsPushModalOpen(true);
  };

  const closePushModal = () => {
    if (pushLoading) return;
    setIsPushModalOpen(false);
    setPushError('');
    setPushSuccessId(null);
  };

  const handleSendPush = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pushTitle.trim() || !pushMessage.trim()) {
      setPushError(t('clients.pushRequired'));
      return;
    }

    setPushLoading(true);
    setPushError('');
    setPushSuccessId(null);

    const clientsFilter = {
      searchQuery,
      status: statusFilter || null,
      hasOrders: appliedFilters.hasOrders ?? null,
      onlyCancelledOrders: appliedFilters.onlyCancelledOrders ?? null,
      lastCompletedOrderBefore: appliedFilters.lastCompletedOrderBefore ?? null,
      lastCompletedOrderAfter: appliedFilters.lastCompletedOrderAfter ?? null,
      lastCompletedOrderBetweenStart: appliedFilters.lastCompletedOrderBetweenStart ?? null,
      lastCompletedOrderBetweenEnd: appliedFilters.lastCompletedOrderBetweenEnd ?? null,
      lastCompletedOrderWithinDays: appliedFilters.lastCompletedOrderWithinDays ?? null,
      minCompletedOrders: appliedFilters.minCompletedOrders ?? null,
      maxCompletedOrders: appliedFilters.maxCompletedOrders ?? null,
      orderStatusIn: appliedFilters.orderStatusIn.length > 0 ? appliedFilters.orderStatusIn : null,
      createdAtFrom: appliedFilters.createdAtFrom ?? null,
      createdAtTo: appliedFilters.createdAtTo ?? null,
      platform: appliedFilters.platform ?? null
    };

    try {
      const response = await clientsService.sendClientsNotification({
        topic: 'SPECIFIC_CLIENTS',
        title: pushTitle.trim(),
        message: pushMessage.trim(),
        imageUrl: pushImageUrl.trim() ? pushImageUrl.trim() : null,
        clientsFilter
      });

      if (response.success && typeof response.data === 'number') {
        setPushSuccessId(response.data);
        setPushTitle('');
        setPushMessage('');
        setPushImageUrl('');
        setPushError('');
        setIsPushModalOpen(false);
      } else {
        setPushError(response.error || t('clients.pushFailed'));
      }
    } catch (err) {
      setPushError(t('clients.pushFailed'));
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header with-actions">
        <div>
          <h1>
            {t('clients.title')}
            {clientsTotal !== null && clientsTotalPages !== null && clientsCurrentPage !== null && (
              <span className="clients-title-meta">
                {' '}
                ({t('clients.countLabel', {
                  totalClients: clientsTotal,
                  currentPage: clientsCurrentPage + 1,
                  totalPages: clientsTotalPages
                })})
              </span>
            )}
          </h1>
          <p>{t('clients.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button
            className="header-action-button"
            onClick={() => navigate('/clients/telegram-bot-chats')}
          >
            {t('clients.telegramBotChatsButton')}
          </button>
          <button
            className="header-action-button"
            onClick={openPushModal}
          >
            {t('clients.sendPushMessage')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Search Section */}
        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-controls">
              <input
                type="text"
                name="search"
                placeholder={t('clients.searchClients')}
                defaultValue={searchQuery}
                className="search-input"
              />
              <button type="submit" className="search-button">
                {t('common.search')}
              </button>
            </div>
          </form>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-header">
            <h3>{t('clients.status')}</h3>
            <div className="filters-header-actions">
              {hasAdvancedParams && (
                <button type="button" className="filter-reset" onClick={clearFilters}>
                  {t('clients.revokeFilters')}
                </button>
              )}
              <button
                type="button"
                className="filters-toggle"
                onClick={() => setShowMoreFilters((prev) => !prev)}
              >
                {showMoreFilters ? t('clients.hideAdvanced') : t('clients.advanced')}
              </button>
            </div>
          </div>
          <div className="filter-tags">
            <button
              className={`filter-tag ${!statusFilter ? 'active' : ''}`}
              onClick={() => updateStatusFilter(null)}
            >
              {t('clients.all')}
            </button>
            <button
              className={`filter-tag ${statusFilter === ClientStatus.ACTIVE ? 'active' : ''}`}
              onClick={() => updateStatusFilter(ClientStatus.ACTIVE)}
            >
              {t('clients.active')}
            </button>
            <button
              className={`filter-tag ${statusFilter === ClientStatus.BLOCKED ? 'active' : ''}`}
              onClick={() => updateStatusFilter(ClientStatus.BLOCKED)}
            >
              {t('clients.blocked')}
            </button>
          </div>

          {showMoreFilters ? (
            <div className="more-filters">
              <div className="filters-grid">
                <div className="filter-field">
                  <label className="filter-label">{t('clients.hasOrders')}</label>
                  <select
                    className="filter-select"
                    value={filters.hasOrders}
                    onChange={(e) => updateFilters({ hasOrders: e.target.value as BooleanFilter })}
                  >
                    <option value="">{t('common.all')}</option>
                    <option value="true">{t('common.yes')}</option>
                    <option value="false">{t('common.no')}</option>
                  </select>
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.onlyCancelledOrders')}</label>
                  <select
                    className="filter-select"
                    value={filters.onlyCancelledOrders}
                    disabled={hasOrdersFalse}
                    onChange={(e) => updateFilters({ onlyCancelledOrders: e.target.value as BooleanFilter })}
                  >
                    <option value="">{t('common.all')}</option>
                    <option value="true">{t('common.yes')}</option>
                    <option value="false">{t('common.no')}</option>
                  </select>
                  {hasOrdersFalse && <div className="filter-hint">{t('clients.onlyCancelledHint')}</div>}
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.platform')}</label>
                  <select
                    className="filter-select"
                    value={filters.platform}
                    onChange={(e) => updateFilters({ platform: e.target.value as ClientPlatform | '' })}
                  >
                    <option value="">{t('common.all')}</option>
                    {Object.values(ClientPlatform).map((platform) => (
                      <option key={platform} value={platform}>
                        {platform}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-field filter-field-wide">
                  <label className="filter-label">{t('clients.orderStatusIn')}</label>
                  <div className="filter-checkbox-group">
                    {Object.values(OrderStatus).map((status) => (
                      <label key={status} className={`filter-checkbox ${orderStatusDisabled ? 'disabled' : ''}`}>
                        <input
                          type="checkbox"
                          disabled={orderStatusDisabled}
                          checked={filters.orderStatusIn.includes(status)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...filters.orderStatusIn, status]
                              : filters.orderStatusIn.filter((value) => value !== status);
                            updateFilters({ orderStatusIn: next });
                          }}
                        />
                        <span>{status}</span>
                      </label>
                    ))}
                  </div>
                  {orderStatusDisabled && <div className="filter-hint">{t('clients.orderStatusHint')}</div>}
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.minCompletedOrders')}</label>
                  <input
                    type="number"
                    min={0}
                    className="filter-input"
                    value={filters.minCompletedOrders}
                    disabled={completedFiltersDisabled}
                    onChange={(e) => updateFilters({ minCompletedOrders: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.maxCompletedOrders')}</label>
                  <input
                    type="number"
                    min={0}
                    className="filter-input"
                    value={filters.maxCompletedOrders}
                    disabled={completedFiltersDisabled}
                    onChange={(e) => updateFilters({ maxCompletedOrders: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.lastCompletedOrderAfter')}</label>
                  <input
                    type="datetime-local"
                    className="filter-input"
                    value={filters.lastCompletedOrderAfter}
                    disabled={lastCompletedFiltersDisabled || filters.lastCompletedOrderWithinDays !== ''}
                    onChange={(e) => updateFilters({ lastCompletedOrderAfter: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.lastCompletedOrderBefore')}</label>
                  <input
                    type="datetime-local"
                    className="filter-input"
                    value={filters.lastCompletedOrderBefore}
                    disabled={lastCompletedFiltersDisabled || filters.lastCompletedOrderWithinDays !== ''}
                    onChange={(e) => updateFilters({ lastCompletedOrderBefore: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.lastCompletedOrderBetweenStart')}</label>
                  <input
                    type="datetime-local"
                    className="filter-input"
                    value={filters.lastCompletedOrderBetweenStart}
                    disabled={lastCompletedFiltersDisabled || filters.lastCompletedOrderWithinDays !== ''}
                    onChange={(e) => updateFilters({ lastCompletedOrderBetweenStart: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.lastCompletedOrderBetweenEnd')}</label>
                  <input
                    type="datetime-local"
                    className="filter-input"
                    value={filters.lastCompletedOrderBetweenEnd}
                    disabled={lastCompletedFiltersDisabled || filters.lastCompletedOrderWithinDays !== ''}
                    onChange={(e) => updateFilters({ lastCompletedOrderBetweenEnd: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.lastCompletedOrderWithinDays')}</label>
                  <input
                    type="number"
                    min={1}
                    className="filter-input"
                    value={filters.lastCompletedOrderWithinDays}
                    disabled={withinDaysDisabled}
                    onChange={(e) => updateFilters({ lastCompletedOrderWithinDays: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.createdAtFrom')}</label>
                  <input
                    type="datetime-local"
                    className="filter-input"
                    value={filters.createdAtFrom}
                    onChange={(e) => updateFilters({ createdAtFrom: e.target.value })}
                  />
                </div>

                <div className="filter-field">
                  <label className="filter-label">{t('clients.createdAtTo')}</label>
                  <input
                    type="datetime-local"
                    className="filter-input"
                    value={filters.createdAtTo}
                    onChange={(e) => updateFilters({ createdAtTo: e.target.value })}
                  />
                </div>
              </div>

              {(filtersError || validationError) && (
                <div className="filter-error">{filtersError || validationError}</div>
              )}

              <div className="filter-actions">
                <button
                  type="button"
                  className="search-button"
                  onClick={() => applyFilters()}
                  disabled={Boolean(validationError)}
                >
                  {t('clients.applyFilters')}
                </button>
                <button type="button" className="filter-reset" onClick={clearFilters}>
                  {t('common.reset')}
                </button>
              </div>
            </div>
          ) : (
            <div className="filters-info-panel">
              <div className="filters-info-header">
                <div className="filters-info-title">{t('clients.filtersInfoTitle')}</div>
                <button
                  type="button"
                  className="filters-info-toggle"
                  onClick={() => setShowInfoExamples((prev) => !prev)}
                >
                  {showInfoExamples ? t('clients.collapseInfo') : t('clients.expandInfo')}
                </button>
              </div>
              {showInfoExamples && (
                <div className="filters-info-list">
                <div className="filters-info-item">
                  <div className="filters-info-text">{t('clients.examples.noOrders')}</div>
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'false',
                        onlyCancelledOrders: '',
                        orderStatusIn: [],
                        minCompletedOrders: '',
                        maxCompletedOrders: '',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">{t('clients.examples.onlyCancelled')}</div>
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'true',
                        onlyCancelledOrders: 'true',
                        minCompletedOrders: '',
                        maxCompletedOrders: '',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">{t('clients.examples.successfulOrders')}</div>
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        minCompletedOrders: '1',
                        maxCompletedOrders: '',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">
                    {t('clients.examples.successfulAfterDate', { date: formattedAfterDate })}
                  </div>
                  <input
                    type="datetime-local"
                    className="filters-info-input"
                    value={infoAfterDate}
                    onChange={(e) => setInfoAfterDate(e.target.value)}
                  />
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'true',
                        onlyCancelledOrders: 'false',
                        minCompletedOrders: '1',
                        maxCompletedOrders: '',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: formatDateTimeLocalUzt(infoAfterEpoch),
                        lastCompletedOrderBetweenEnd: formatDateTimeLocalUzt(nowEpoch),
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">
                    {t('clients.examples.orderedLastDays', { days: infoDaysOrderedValue })}
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="filters-info-input"
                    value={infoDaysOrdered}
                    onChange={(e) => setInfoDaysOrdered(e.target.value)}
                  />
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'true',
                        onlyCancelledOrders: 'false',
                        lastCompletedOrderWithinDays: `${infoDaysOrderedValue}`,
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        minCompletedOrders: '',
                        maxCompletedOrders: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">
                    {t('clients.examples.noOrderLastDays', { days: infoDaysNoOrderValue })}
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="filters-info-input"
                    value={infoDaysNoOrder}
                    onChange={(e) => setInfoDaysNoOrder(e.target.value)}
                  />
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'true',
                        onlyCancelledOrders: 'false',
                        minCompletedOrders: '1',
                        maxCompletedOrders: '',
                        lastCompletedOrderBetweenStart: formatDateTimeLocalUzt(getUztStartOfDayEpoch(0)),
                        lastCompletedOrderBetweenEnd: formatDateTimeLocalUzt(noOrderDaysStart),
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderWithinDays: '',
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">
                    {t('clients.examples.newUsers', { days: infoDaysNewValue })}
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="filters-info-input"
                    value={infoDaysNewClients}
                    onChange={(e) => setInfoDaysNewClients(e.target.value)}
                  />
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        createdAtFrom: formatDateTimeLocalUzt(newClientsStart),
                        createdAtTo: '',
                        hasOrders: '',
                        onlyCancelledOrders: '',
                        orderStatusIn: [],
                        minCompletedOrders: '',
                        maxCompletedOrders: '',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">
                    {t('clients.examples.recentNoOrders', { days: infoDaysNewValue })}
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="filters-info-input"
                    value={infoDaysNewClients}
                    onChange={(e) => setInfoDaysNewClients(e.target.value)}
                  />
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        createdAtFrom: formatDateTimeLocalUzt(newClientsStart),
                        createdAtTo: '',
                        hasOrders: 'false',
                        onlyCancelledOrders: '',
                        orderStatusIn: [],
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: '',
                        minCompletedOrders: '',
                        maxCompletedOrders: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">
                    {t('clients.examples.veryActive', { count: infoVeryActiveValue })}
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="filters-info-input"
                    value={infoVeryActiveMin}
                    onChange={(e) => setInfoVeryActiveMin(e.target.value)}
                  />
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'true',
                        onlyCancelledOrders: 'false',
                        minCompletedOrders: `${infoVeryActiveValue}`,
                        maxCompletedOrders: '',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>

                <div className="filters-info-item">
                  <div className="filters-info-text">{t('clients.examples.oneTime')}</div>
                  <button
                    type="button"
                    className="filters-info-apply"
                    onClick={() =>
                      applyExample({
                        hasOrders: 'true',
                        onlyCancelledOrders: 'false',
                        minCompletedOrders: '1',
                        maxCompletedOrders: '1',
                        lastCompletedOrderBefore: '',
                        lastCompletedOrderAfter: '',
                        lastCompletedOrderBetweenStart: '',
                        lastCompletedOrderBetweenEnd: '',
                        lastCompletedOrderWithinDays: ''
                      })
                    }
                  >
                    {t('clients.examples.apply')}
                  </button>
                </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Clients Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('clients.loadingClients')}</div>
          ) : clients.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('clients.clientId')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('clients.platform')}</th>
                  <th>{t('clients.status')}</th>
                  <th>{t('clients.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={6} className="no-data">
                    {t('clients.noClients')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('clients.clientId')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('clients.platform')}</th>
                  <th>{t('clients.status')}</th>
                  <th>{t('clients.createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => handleRowClick(client.id)}
                    className="clickable-row"
                  >
                    <td>{client.id}</td>
                    <td>{client.name || <span style={{ color: '#999' }}>No name</span>}</td>
                    <td>{client.phone}</td>
                    <td>
                      {client.platform ? (
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            backgroundColor: client.platform === ClientPlatform.ANDROID ? '#3DDC84' : '#007AFF',
                            color: '#fff'
                          }}
                        >
                          {client.platform}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          backgroundColor: client.status === ClientStatus.ACTIVE ? '#d4edda' : '#f8d7da',
                          color: client.status === ClientStatus.ACTIVE ? '#155724' : '#721c24'
                        }}
                      >
                        {getLocalizedStatus(client.status)}
                      </span>
                    </td>
                    <td>{formatDate(client.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && clients.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">{t('orders.page')} {currentPage + 1}</span>
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage + 1)}
              disabled={clients.length < pageSize}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      <Modal
        isOpen={isPushModalOpen}
        onClose={closePushModal}
        title={t('clients.pushModalTitle')}
      >
        <form className="modal-form" onSubmit={handleSendPush}>
          <div className="modal-form-group">
            <label htmlFor="pushTitle">{t('clients.pushTitleLabel')}</label>
            <input
              id="pushTitle"
              type="text"
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              disabled={pushLoading}
              placeholder={t('clients.pushTitlePlaceholder')}
            />
          </div>
          <div className="modal-form-group">
            <label htmlFor="pushMessage">{t('clients.pushMessageLabel')}</label>
            <textarea
              id="pushMessage"
              value={pushMessage}
              onChange={(e) => setPushMessage(e.target.value)}
              disabled={pushLoading}
              placeholder={t('clients.pushMessagePlaceholder')}
            />
          </div>
          <div className="modal-form-group">
            <label htmlFor="pushImageUrl">{t('clients.pushImageUrlLabel')}</label>
            <input
              id="pushImageUrl"
              type="text"
              value={pushImageUrl}
              onChange={(e) => setPushImageUrl(e.target.value)}
              disabled={pushLoading}
              placeholder={t('clients.pushImageUrlPlaceholder')}
            />
          </div>
          {pushError && <div className="error-message">{pushError}</div>}
          {pushSuccessId !== null && (
            <div className="success-message">
              {t('clients.pushSuccess', { id: pushSuccessId })}
            </div>
          )}
          <div className="modal-actions">
            <button type="button" className="modal-button secondary" onClick={closePushModal} disabled={pushLoading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="modal-button primary" disabled={pushLoading}>
              {pushLoading ? t('clients.pushSending') : t('clients.pushSend')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Clients;
